// src/auth/Login.js
import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, Container, Alert, Row, Col, Card } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { login, requestPasswordReset, resetPassword } from '../features/auth/authSlice';
import '../assets/styles/Login.css';
import loginImage from '../assets/images/auth1.png';
import { useDispatch, useSelector } from 'react-redux';
import { setLanguage } from '../features/language/languageSlice';
import normalizeLocale from '../utils/i18n-utils';
import { useLocation } from 'react-router-dom';

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // 从 Redux 获取认证状态
  const { accessToken, user, _persist } = useSelector((state) => state.auth);
  const rehydrated = _persist?.rehydrated;

  const isAuthenticated = Boolean(accessToken);
  const urlToken = useMemo(() => params.get('token') || '', [params]);

  // 初始模式
  const location = useLocation();
  const isSetPasswordRoute = location.pathname === '/set-password';

  // 表单状态
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [resetPwd1, setResetPwd1] = useState('');
  const [resetPwd2, setResetPwd2] = useState('');
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [mode, setMode] = useState('login');

  // ✅ 让 mode 跟 URL 同步（行业标准）
  useEffect(() => {
    if (isSetPasswordRoute || urlToken) setMode('reset');
    else setMode('login');
  }, [isSetPasswordRoute, urlToken]);

  // ✅ /set-password 必须带 token
  useEffect(() => {
    if (isSetPasswordRoute && !urlToken) {
      setError('Invalid or missing token');
      navigate('/login', { replace: true });
    }
  }, [isSetPasswordRoute, urlToken, navigate]);

  // 主效果：已认证时重定向到 dashboard
  useEffect(() => {
    // 等待 rehydrated 完成
    if (!rehydrated) {
      console.log('⏳ Waiting for rehydration...');
      return;
    }

    // 如果有重置密码 token，不重定向
    if (isSetPasswordRoute || urlToken) return;

    // 如果已认证，重定向到 dashboard
    if (isAuthenticated) {
      console.log('✅ Already authenticated, redirecting to /dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [rehydrated, isAuthenticated, isSetPasswordRoute, urlToken, navigate]);

  // 检测浏览器语言
  useEffect(() => {
    const browserLang = navigator.language || 'en';
    const languageMap = {
      'es': 'es',
      'pt': 'pt',
      'en': 'en',
      'fr': 'fr',
      'it': 'it',
      'de': 'de'
    };

    const defaultLang = languageMap[browserLang.split('-')[0]] || 'en';
    dispatch(setLanguage(defaultLang));
  }, [dispatch]);

  // 登录处理函数
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    try {
      const resultAction = await dispatch(login({
        email,
        password,
        rememberMe
      }));

      if (login.fulfilled.match(resultAction)) {
        console.log('✅ Login successful');

        // 设置用户语言
        const userLang = normalizeLocale(resultAction.payload.user?.defaultLocale);
        dispatch(setLanguage(userLang));

        // 登录成功后跳转到 Dashboard
        navigate('/dashboard');
      } else {
        console.log('❌ Login failed');
        setError(resultAction.payload || 'Invalid email or password');
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      setError('Invalid email or password');
    }
  };

  // 请求重置密码邮件
  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    const resultAction = await dispatch(requestPasswordReset(email));

    if (requestPasswordReset.fulfilled.match(resultAction)) {
      setSuccessMsg('If that email exists, we\'ve sent a reset link.');
    } else {
      setError(resultAction.payload || 'Something went wrong');
    }
  };

  // 重置密码
  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (!urlToken) {
      setError('Invalid link');
      return;
    }

    if (resetPwd1 !== resetPwd2) {
      setError('Passwords do not match');
      return;
    }

    if (resetPwd1.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    const resultAction = await dispatch(resetPassword({
      token: urlToken,
      newPassword: resetPwd1
    }));

    if (resetPassword.fulfilled.match(resultAction)) {
      setSuccessMsg('Password updated. You can log in now.');
      setResetPwd1('');
      setResetPwd2('');
      navigate('/login', { replace: true }); // ✅ 清掉 token
    } else {
      setError(resultAction.payload || 'Failed to reset password');
    }
  };

  // 渲染部分保持不变
  return (
    <div className="vh-100 bg-account-pages">
      <Container>
        <Row className="g-0 justify-content-center">
          <Col xl={6} lg={8} className="h-80">
            <Card className="h-100">
              <Card.Img
                variant="top"
                className="img-fluid auth-img d-none d-sm-block"
                src="static/picture/logo-dark.png"
                alt=""
              />
              <Card.Body className="d-flex align-items-center">
                <div className="py-sm-5 px-sm-5 rounded-3" style={{ width: '100%' }}>
                  <div className="mb-4 text-center">
                    <h2 className="pt-2 mb-0">
                      {mode === 'login' && 'Welcome to Rentalmotorbike'}
                      {mode === 'forgot' && 'Reset your password'}
                      {mode === 'reset' && 'Set a new password'}
                    </h2>
                    {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                    {successMsg && <Alert variant="success" className="mt-3">{successMsg}</Alert>}
                    <p className="text-muted">
                      {mode === 'login' && 'Sign in to continue'}
                      {mode === 'forgot' && 'Enter your email to receive a reset link'}
                      {mode === 'reset' && 'Please choose a strong password'}
                    </p>
                  </div>

                  {/* 登录表单 */}
                  {mode === 'login' && (
                    <Form onSubmit={handleLogin}>
                      <Row>
                        <Col xs={12}>
                          <Form.Group controlId="email" className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={12}>
                          <Form.Group controlId="password" className="mb-3">
                            <Form.Label>Password</Form.Label>
                            <Form.Control
                              type="password"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col sm={6}>
                          <Form.Check
                            type="checkbox"
                            id="rememberMeCheckbox"
                            label="Remember me"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                          />
                        </Col>
                        <Col sm={6} className="text-sm-end">
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => {
                              setError('');
                              setSuccessMsg('');
                              setMode('forgot');
                            }}
                          >
                            <i className="mdi mdi-lock me-1"></i> Forgot password?
                          </button>
                        </Col>
                      </Row>
                      <div className="mt-4">
                        <Button variant="primary" className="w-100" type="submit">
                          Log in
                        </Button>
                      </div>
                    </Form>
                  )}

                  {/* 忘记密码表单 */}
                  {mode === 'forgot' && (
                    <Form onSubmit={handleForgot}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </Form.Group>
                      <div className="d-flex gap-2">
                        <Button type="submit" className="flex-grow-1">Send reset link</Button>
                        <Button
                          variant="outline-secondary"
                          type="button"
                          onClick={() => {
                            setMode('login');
                            setError('');
                            setSuccessMsg('');
                          }}
                        >
                          Back to login
                        </Button>
                      </div>
                    </Form>
                  )}

                  {/* 重置密码表单 */}
                  {mode === 'reset' && (
                    <Form onSubmit={handleReset}>
                      <Form.Group className="mb-3">
                        <Form.Label>New Password</Form.Label>
                        <Form.Control
                          type="password"
                          value={resetPwd1}
                          onChange={(e) => setResetPwd1(e.target.value)}
                          required
                        />
                      </Form.Group>
                      <Form.Group className="mb-4">
                        <Form.Label>Confirm Password</Form.Label>
                        <Form.Control
                          type="password"
                          value={resetPwd2}
                          onChange={(e) => setResetPwd2(e.target.value)}
                          required
                        />
                      </Form.Group>
                      <div className="d-flex gap-2">
                        <Button type="submit" className="flex-grow-1">Update password</Button>
                        <Button
                          variant="outline-secondary"
                          type="button"
                          onClick={() => {
                            setMode('login');
                            setError('');
                            setSuccessMsg('');
                          }}
                        >
                          Back to login
                        </Button>
                      </div>
                    </Form>
                  )}

                  <div className="mt-4 text-center">
                    <p className="mb-0 text-muted">
                      © {new Date().getFullYear()} Rentalmotorbike. All rights reserved.
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={6}>
            <div className="bg-primary">
              <img
                src={loginImage}
                className="img-fluid d-xl-block d-none custom-img"
                alt=""
              />
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Login;